import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { Upload, X, CheckCircle, AlertCircle } from 'lucide-react';
import { videoApi } from '../services/api';
import type { Video } from '../services/api';

interface VideoUploadProps {
  onUploadComplete?: (video: Video) => void;
  onCancel?: () => void;
}

interface VideoFormData {
  title: string;
  description: string;
  category: 'strength' | 'cardio' | 'mobility' | 'flexibility' | 'other';
  difficulty: 'beginner' | 'intermediate' | 'advanced';
}

const MAX_FILE_SIZE = 5 * 1024 * 1024 * 1024; // 5GB in bytes
const ALLOWED_FORMATS = ['video/mp4', 'video/quicktime', 'video/x-msvideo'];

export function VideoUpload({ onUploadComplete, onCancel }: VideoUploadProps) {
  const queryClient = useQueryClient();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState<'idle' | 'uploading' | 'processing' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState<string>('');

  const { register, handleSubmit, formState: { errors }, reset } = useForm<VideoFormData>({
    defaultValues: {
      category: 'strength',
      difficulty: 'beginner',
    }
  });

  // Create video metadata mutation
  const createVideoMutation = useMutation({
    mutationFn: (data: Partial<Video>) => videoApi.create(data),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['videos'] });
      setUploadStatus('success');
      if (onUploadComplete) {
        onUploadComplete(response.data);
      }
      // Reset form after 2 seconds
      setTimeout(() => {
        reset();
        setSelectedFile(null);
        setUploadProgress(0);
        setUploadStatus('idle');
      }, 2000);
    },
    onError: (error: any) => {
      setUploadStatus('error');
      setErrorMessage(error?.response?.data?.message || error.message || 'Failed to save video metadata');
    },
  });

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file format
    if (!ALLOWED_FORMATS.includes(file.type)) {
      setErrorMessage('Invalid file format. Please upload MP4, MOV, or AVI files.');
      setUploadStatus('error');
      return;
    }

    // Validate file size
    if (file.size > MAX_FILE_SIZE) {
      setErrorMessage('File size exceeds 5GB limit.');
      setUploadStatus('error');
      return;
    }

    setSelectedFile(file);
    setErrorMessage('');
    setUploadStatus('idle');
  };

  const uploadToMux = async (file: File): Promise<{ assetId: string; playbackId: string; duration: number }> => {
    const uploadUrlResponse = await videoApi.createUploadUrl();
    const { upload_url, upload_id } = uploadUrlResponse.data;

    if (!upload_id || !upload_url) {
      throw new Error(
        'Invalid upload response. Ensure the backend is updated and returns upload_id and upload_url. ' +
        `Received keys: ${Object.keys(uploadUrlResponse.data || {}).join(', ')}`
      );
    }

    // Upload file to Mux with progress tracking
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable) {
          const progress = Math.round((event.loaded / event.total) * 100);
          setUploadProgress(progress);
        }
      });

      xhr.addEventListener('load', async () => {
        if (xhr.status === 200 || xhr.status === 201) {
          setUploadStatus('processing');

          // Poll for asset readiness (Mux processes upload + encoding; duration is accurate when ready)
          const pollInterval = 2000;
          const maxAttempts = 150; // ~5 minutes (encoding can take 1-2x video length)

          const poll = async (attempt: number): Promise<void> => {
            try {
              const statusResponse = await videoApi.getUploadStatus(upload_id);
              const { ready, asset_id, playback_id, duration_seconds } = statusResponse.data;

              if (ready && asset_id && playback_id && duration_seconds !== undefined) {
                resolve({
                  assetId: asset_id,
                  playbackId: playback_id,
                  duration: Math.max(1, duration_seconds),
                });
                return;
              }

              if (attempt >= maxAttempts) {
                reject(new Error('Upload processing timed out. Please try again.'));
                return;
              }

              setTimeout(() => poll(attempt + 1), pollInterval);
            } catch (err) {
              const errObj = err as { response?: { status?: number; data?: { message?: string } } };
              const msg = errObj?.response?.status === 404
                ? 'Upload status endpoint not found. Ensure the backend is updated and restarted.'
                : errObj?.response?.data?.message || (err instanceof Error ? err.message : 'Failed to check upload status');
              reject(new Error(msg));
            }
          };

          poll(0);
        } else {
          reject(new Error('Upload failed'));
        }
      });

      xhr.addEventListener('error', () => {
        reject(new Error('Upload failed'));
      });

      xhr.open('PUT', upload_url);
      xhr.setRequestHeader('Content-Type', file.type);
      xhr.send(file);
    });
  };

  const onSubmit = async (data: VideoFormData) => {
    if (!selectedFile) {
      setErrorMessage('Please select a video file');
      return;
    }

    try {
      setUploadStatus('uploading');
      setErrorMessage('');

      // Upload to Mux
      const { assetId, playbackId, duration } = await uploadToMux(selectedFile);

      // Create video metadata in backend
      createVideoMutation.mutate({
        title: data.title,
        description: data.description,
        category: data.category,
        difficulty: data.difficulty,
        mux_asset_id: assetId,
        mux_playback_id: playbackId,
        duration_seconds: duration,
      });
    } catch (error: any) {
      setUploadStatus('error');
      setErrorMessage(error.message || 'Upload failed');
    }
  };

  const handleCancel = () => {
    if (uploadStatus === 'uploading' || uploadStatus === 'processing') {
      if (!confirm('Upload in progress. Are you sure you want to cancel?')) {
        return;
      }
    }
    reset();
    setSelectedFile(null);
    setUploadProgress(0);
    setUploadStatus('idle');
    setErrorMessage('');
    if (onCancel) {
      onCancel();
    }
  };

  const isUploading = uploadStatus === 'uploading' || uploadStatus === 'processing';
  const isSuccess = uploadStatus === 'success';

  return (
    <div className="bg-white shadow rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-gray-900">Upload Video</h2>
        {onCancel && (
          <button
            type="button"
            onClick={handleCancel}
            disabled={isUploading}
            className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
          >
            <X className="w-5 h-5" />
          </button>
        )}
      </div>

      {errorMessage && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded flex items-start">
          <AlertCircle className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5" />
          <span>{errorMessage}</span>
        </div>
      )}

      {isSuccess && (
        <div className="mb-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded flex items-start">
          <CheckCircle className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5" />
          <span>Video uploaded successfully!</span>
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* File Upload */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Video File
          </label>
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-indigo-500 transition-colors">
            <input
              type="file"
              accept="video/mp4,video/quicktime,video/x-msvideo"
              onChange={handleFileSelect}
              disabled={isUploading}
              className="hidden"
              id="video-upload"
            />
            <label
              htmlFor="video-upload"
              className="cursor-pointer flex flex-col items-center"
            >
              <Upload className="w-12 h-12 text-gray-400 mb-2" />
              {selectedFile ? (
                <div>
                  <p className="text-sm font-medium text-gray-900">{selectedFile.name}</p>
                  <p className="text-xs text-gray-500 mt-1">
                    {(selectedFile.size / (1024 * 1024)).toFixed(2)} MB
                  </p>
                </div>
              ) : (
                <div>
                  <p className="text-sm text-gray-600">Click to select a video file</p>
                  <p className="text-xs text-gray-500 mt-1">
                    MP4, MOV, or AVI (max 5GB)
                  </p>
                </div>
              )}
            </label>
          </div>
        </div>

        {/* Upload Progress */}
        {isUploading && (
          <div>
            <div className="flex justify-between text-sm text-gray-600 mb-2">
              <span>
                {uploadStatus === 'uploading' ? 'Uploading...' : 'Processing...'}
              </span>
              <span>{uploadProgress}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-indigo-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${uploadProgress}%` }}
              />
            </div>
          </div>
        )}

        {/* Video Metadata */}
        <div>
          <label htmlFor="title" className="block text-sm font-medium text-gray-700">
            Title
          </label>
          <input
            id="title"
            type="text"
            {...register('title', {
              required: 'Title is required',
              maxLength: { value: 200, message: 'Title must be less than 200 characters' }
            })}
            disabled={isUploading}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm disabled:bg-gray-100"
            placeholder="e.g., Barbell Squat Tutorial"
          />
          {errors.title && (
            <p className="mt-1 text-sm text-red-600">{errors.title.message}</p>
          )}
        </div>

        <div>
          <label htmlFor="description" className="block text-sm font-medium text-gray-700">
            Description
          </label>
          <textarea
            id="description"
            rows={4}
            {...register('description', {
              maxLength: { value: 1000, message: 'Description must be less than 1000 characters' }
            })}
            disabled={isUploading}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm disabled:bg-gray-100"
            placeholder="Describe the exercise, form cues, and any important notes..."
          />
          {errors.description && (
            <p className="mt-1 text-sm text-red-600">{errors.description.message}</p>
          )}
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label htmlFor="category" className="block text-sm font-medium text-gray-700">
              Category
            </label>
            <select
              id="category"
              {...register('category', { required: 'Category is required' })}
              disabled={isUploading}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm disabled:bg-gray-100"
            >
              <option value="strength">Strength</option>
              <option value="cardio">Cardio</option>
              <option value="mobility">Mobility</option>
              <option value="flexibility">Flexibility</option>
              <option value="other">Other</option>
            </select>
            {errors.category && (
              <p className="mt-1 text-sm text-red-600">{errors.category.message}</p>
            )}
          </div>

          <div>
            <label htmlFor="difficulty" className="block text-sm font-medium text-gray-700">
              Difficulty
            </label>
            <select
              id="difficulty"
              {...register('difficulty', { required: 'Difficulty is required' })}
              disabled={isUploading}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm disabled:bg-gray-100"
            >
              <option value="beginner">Beginner</option>
              <option value="intermediate">Intermediate</option>
              <option value="advanced">Advanced</option>
            </select>
            {errors.difficulty && (
              <p className="mt-1 text-sm text-red-600">{errors.difficulty.message}</p>
            )}
          </div>
        </div>

        {/* Submit Button */}
        <div className="flex justify-end gap-3">
          {onCancel && (
            <button
              type="button"
              onClick={handleCancel}
              disabled={isUploading}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
          )}
          <button
            type="submit"
            disabled={isUploading || !selectedFile}
            className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            {isUploading ? 'Uploading...' : 'Upload Video'}
          </button>
        </div>
      </form>
    </div>
  );
}
